// src/ine/ine_config/pages/IneRegistrersPage.jsx
import React, { useEffect, useRef, useState } from "react";
import AutoDismissAlert from "../../../components/autoDismiss";
import { API_URLS } from "../../../api/apiConfig";
import { postWithAuth } from "../../../api/api";
import Form from "react-bootstrap/Form";
import { Modal, Button } from "react-bootstrap"; // ⬅️ nuevo

const DATA_SOURCE_OPTIONS = [
  { value: "", label: "Todas las fuentes" },
  { value: "PEX", label: "PEX" },
  { value: "RR", label: "RoomRaccoon" },
  { value: "REZ", label: "StarRez" },
];

const ERROR_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "true", label: "Con errores" },
  { value: "false", label: "Sin errores" },
];

const IneRegistrersPage = () => {
  // tabla
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  // estado de UI
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("info");
  const [showAlert, setShowAlert] = useState(false);

  // filtros
  const [searchText, setSearchText] = useState("");
  const [dataSource, setDataSource] = useState("");
  const [hasErrors, setHasErrors] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [monthInput, setMonthInput] = useState(getPrevMonthISO());

  // modal huéspedes
  const [showGuestsModal, setShowGuestsModal] = useState(false);
  const [selectedGuests, setSelectedGuests] = useState([]);
  const [selectedRegistrerId, setSelectedRegistrerId] = useState(null);

  function openAlert(msg, type = "danger") {
    setMessage(msg);
    setAlertType(type);
    setShowAlert(true);
  }

  function parseMonthYear(monthStr) {
    if (!monthStr) return { month: undefined, year: undefined };
    const [y, m] = monthStr.split("-").map(Number);
    if (!y || !m) return { month: undefined, year: undefined };
    return { month: m, year: y };
  }

  async function fetchData() {
    setIsLoading(true);
    try {
      const { month, year } = parseMonthYear(monthInput);

      const payload = {
        page,
        limit,
        search_text: searchText || undefined,
        month: month || undefined,
        year: year || undefined,
        data_source: dataSource || undefined,
        has_errors:
          hasErrors === "" ? undefined : hasErrors === "true" ? true : false,
      };

      const resp = await postWithAuth(API_URLS.LIST_INE_PEX_REGISTERS, payload);

      if (resp?.message === "success") {
        const data = resp.data || {};
        const list = data.registrers || data.list || [];
        setItems(list);
        setTotal(data.total ?? list.length ?? 0);
      } else {
        openAlert(resp?.detail || "Error al obtener registros INE");
      }
    } catch (e) {
      console.error(e);
      openAlert("Ha ocurrido un error conectando con el servidor");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  // Abrir modal de huéspedes
  const handleOpenGuests = (row) => {
    const guests = Array.isArray(row?.guests) ? row.guests : [];
    setSelectedGuests(guests);
    setSelectedRegistrerId(row?.booking_id || row?._id || null);
    setShowGuestsModal(true);
  };

  return (
    <>
      {showAlert && (
        <div className="mb-5">
          <AutoDismissAlert
            message={message}
            alertType={alertType}
            onClose={() => setShowAlert(false)}
          />
        </div>
      )}

      {/* Filtros (grid consistente y centrado vertical) */}
      <div className="mb-6">
        <Form
          className="row row-cols-auto g-3 align-items-center"
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            fetchData();
          }}
        >
          <Form.Group className="col-auto d-flex align-items-center">
            <Form.Label className="mb-0 me-2">Mes</Form.Label>
            <Form.Control
              type="month"
              value={monthInput}
              onChange={(e) => setMonthInput(e.target.value)}
              style={{ minWidth: 160 }}
            />
          </Form.Group>

          <Form.Group className="col-auto d-flex align-items-center">
            <Form.Label className="visually-hidden">Buscar</Form.Label>
            <Form.Control
              type="text"
              placeholder="Booking, estado, fuente…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ minWidth: 260 }}
            />
          </Form.Group>

          <Form.Group className="col-auto d-flex align-items-center">
            <Form.Label className="mb-0 me-2">Fuente</Form.Label>
            <Form.Select
              value={dataSource}
              onChange={(e) => setDataSource(e.target.value)}
              style={{ minWidth: 200 }}
            >
              {DATA_SOURCE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="col-auto d-flex align-items-center">
            <Form.Label className="mb-0 me-2">Errores</Form.Label>
            <Form.Select
              value={hasErrors}
              onChange={(e) => setHasErrors(e.target.value)}
              style={{ minWidth: 180 }}
            >
              {ERROR_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <div className="col-auto d-flex align-items-center">
            <button
              type="submit"
              className="btn btn-sm btn-primary"
              onClick={() => setPage(1)}
            >
              Aplicar filtros
            </button>
          </div>
        </Form>
      </div>

      {/* Tabla */}
      <div className="table-responsive" style={{ overflowX: "auto" }}>
        <table className="table align-middle table-row-dashed fs-6 gy-5">
          <thead>
            <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
              <th>Booking ID</th>
              <th>Tipo INE</th>
              <th>Room ID</th>
              <th>Huéspedes</th>
              <th>Inicio</th>
              <th>Fin</th>
              <th>Origen INE</th>
              <th>Fuente datos</th>
              <th>Precio</th>
              <th>Estado</th>
              <th>Errores</th>
              <th>Acciones</th> {/* ⬅️ nueva */}
            </tr>
          </thead>
          <tbody className="text-gray-600 fw-semibold">
            {isLoading ? (
              <tr>
                <td colSpan="12" className="text-center py-5">
                  <div
                    className="spinner-border text-color-amarillo-livensa"
                    style={{ width: "3rem", height: "3rem" }}
                    role="status"
                  ></div>
                </td>
              </tr>
            ) : (items || []).length > 0 ? (
              items.map((row, idx) => (
                <tr key={row.id || row._id || row.booking_id || idx}>
                  <td>{row.booking_id ?? ""}</td>
                  <td>{row.ine_room_type ?? ""}</td>
                  <td>{row.room_id ?? ""}</td>
                  <td>{Array.isArray(row.guests) ? row.guests.length : 0}</td>
                  <td>{row.booking_start ?? ""}</td>
                  <td>{row.booking_end ?? ""}</td>
                  <td>{row.ine_source ?? ""}</td>
                  <td>{row.data_source ?? ""}</td>
                  <td>{formatMoney(row.price)}</td>
                  <td>{row.status ?? ""}</td>
                  <td>
                    {Array.isArray(row.errors) && row.errors.length > 0
                      ? row.errors.join(", ")
                      : ""}
                  </td>
                  <td>
                    <TableActionsRegistrer
                      onViewGuests={() => handleOpenGuests(row)}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="12" className="text-center py-5">
                  No se encontraron resultados
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      <div className="row mt-4">
        <div className="col-sm-12 col-md-7 d-flex align-items-center justify-content-center justify-content-md-end gap-3">
          <Form.Select
            style={{ width: 180 }}
            value={limit}
            onChange={(e) => {
              setPage(1);
              setLimit(Number(e.target.value));
            }}
          >
            <option value="5">5 por página</option>
            <option value="10">10 por página</option>
            <option value="20">20 por página</option>
            <option value="50">50 por página</option>
          </Form.Select>

          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </button>
              </li>
              <li className="page-item disabled">
                <span className="page-link">
                  Página {page} de {totalPages}
                </span>
              </li>
              <li
                className={`page-item ${page >= totalPages ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setPage((p) => (p < totalPages ? p + 1 : p))}
                >
                  Siguiente
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Modal de Huéspedes */}
      <GuestsModal
        show={showGuestsModal}
        onHide={() => setShowGuestsModal(false)}
        registrerId={selectedRegistrerId}
        guests={selectedGuests}
      />
    </>
  );
};

export default IneRegistrersPage;

/* ======= Acciones por fila (dropdown simple, sin dependencias extra) ======= */
const TableActionsRegistrer = ({ onViewGuests }) => {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  const anchorRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  const toggleDropdown = (e) => {
    e.preventDefault();
    setShowMenu((s) => !s);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !anchorRef.current.contains(event.target)
      ) {
        setShowMenu(false);
      }
    };
    if (showMenu) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);

  useEffect(() => {
    if (showMenu && anchorRef.current) {
      const r = anchorRef.current.getBoundingClientRect();
      setMenuPosition({ x: r.left, y: r.top + r.height + 8 });
    }
  }, [showMenu]);

  return (
    <>
      <a
        href="#"
        className="btn btn-sm btn-light btn-flex btn-center btn-active-light-primary"
        onClick={toggleDropdown}
        ref={anchorRef}
      >
        Acciones
        <i className="ki-outline ki-down text-color-azul-livensa fs-5 ms-1"></i>
      </a>
      <div
        ref={menuRef}
        style={{
          position: "fixed",
          left: menuPosition.x,
          top: menuPosition.y,
          zIndex: 1050,
        }}
        className={`menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary fw-semibold fs-7 w-175px py-4 ${
          showMenu ? "show" : "d-none"
        }`}
        data-kt-menu="true"
      >
        <div className="menu-item px-3">
          <a
            className="menu-link px-3"
            onClick={() => {
              setShowMenu(false);
              onViewGuests();
            }}
          >
            Ver huéspedes
          </a>
        </div>
      </div>
    </>
  );
};

/* ====================== Modal de Huéspedes ====================== */
/* ====================== Modal de Huéspedes (solo is_country y country_province) ====================== */
const GuestsModal = ({ show, onHide, registrerId, guests }) => {
  // guests: array de objetos con { is_country, country_province }
  const safeGuests = Array.isArray(guests) ? guests : [];

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Huéspedes {registrerId ? `· ${registrerId}` : ""}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {safeGuests.length > 0 ? (
          <div className="table-responsive">
            <table className="table align-middle table-row-dashed fs-6 gy-3">
              <thead>
                <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                  <th style={{ width: 60 }}>#</th>
                  <th>Provincia/Región</th>
                  <th>¿Es país? </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {safeGuests.map((g, i) => {
                  const province = g?.country_province ?? "-";
                  const isCountry =
                    typeof g?.is_country === "boolean"
                      ? g.is_country
                        ? "Sí"
                        : "No"
                      : g?.is_country ?? "-";

                  return (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{province}</td>
                      <td>{isCountry}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-muted">No hay huéspedes para este registro.</div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="light" onClick={onHide}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Helpers
function getPrevMonthISO() {
  const now = new Date();
  const firstThis = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastPrev = new Date(firstThis.getTime() - 1);
  const y = lastPrev.getFullYear();
  const m = String(lastPrev.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

function formatMoney(val) {
  const n = Number(val);
  if (Number.isNaN(n)) return val ?? "";
  return n.toLocaleString("es-ES", { style: "currency", currency: "EUR" });
}
